import React, { useState, useEffect, useCallback } from 'react';
import { systemAPI } from '../../services/api';
import '../../components/system/SystemLayout.css';

function BarChart({ data, labelKey, valueKey, color = '#1a237e' }) {
  const max = Math.max(...data.map(d => d[valueKey] || 0), 1);
  return (
    <div className="sys-page sys-dashboard-page">
      {data.map((d, i) => (
        <div key={i} className="sys-bar-row">
          <span className="sys-bar-label" title={d[labelKey]}>
            {String(d[labelKey]).length > 14 ? String(d[labelKey]).slice(0, 13) + '…' : d[labelKey]}
          </span>
          <div className="sys-bar-track">
            <div className="sys-bar-fill" style={{ width: `${(d[valueKey] / max) * 100}%`, background: color }} />
          </div>
          <span className="sys-bar-value">{Number(d[valueKey]).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

export default function SystemDashboard() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const res = await systemAPI.getDashboard();
      setData(res.data?.data || res.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load dashboard');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}>Loading dashboard…</div>;
  if (error)   return <div style={{ textAlign: 'center', padding: 60, color: '#c62828' }}>{error}</div>;

  const s             = data?.summary       || {};
  const charts        = data?.charts        || {};
  const recentTenants = data?.recentTenants || [];

  const byTaluka = (charts.tenantsByTaluka     || []).map(r => ({ taluka: r.taluka || 'Unknown', count: Number(r.count) }));
  const byStatus = (charts.complaintsByStatus  || []).map(r => ({ status: r.status, count: Number(r.count) }));
  const byRole   = (charts.usersByRole         || []).map(r => ({ role: r.role?.replace(/_/g, ' '), count: Number(r.count) }));

  return (
    <div>
      <div className="sys-page-header">
        <div>
          <h1>Platform Dashboard</h1>
          <p>Overview of the entire Digital Grampanchayat system</p>
        </div>
      </div>

      {/* Summary stats */}
      <div className="sys-stat-grid">
        <div className="sys-stat-card">
          <div className="sys-stat-num">{s.totalTenants ?? 0}</div>
          <div className="sys-stat-label">Total GPs</div>
        </div>
        <div className="sys-stat-card green">
          <div className="sys-stat-num">{s.activeTenants ?? 0}</div>
          <div className="sys-stat-label">Active GPs</div>
        </div>
        <div className="sys-stat-card">
          <div className="sys-stat-num">{(s.totalUsers ?? 0).toLocaleString()}</div>
          <div className="sys-stat-label">System Users</div>
        </div>
        <div className="sys-stat-card teal">
          <div className="sys-stat-num">{(s.totalCitizens ?? 0).toLocaleString()}</div>
          <div className="sys-stat-label">Citizens</div>
        </div>
        <div className="sys-stat-card orange">
          <div className="sys-stat-num">{(s.totalComplaints ?? 0).toLocaleString()}</div>
          <div className="sys-stat-label">Total Complaints</div>
        </div>
        <div className="sys-stat-card red">
          <div className="sys-stat-num">{(s.openComplaints ?? 0).toLocaleString()}</div>
          <div className="sys-stat-label">Open Complaints</div>
        </div>
      </div>

      {/* Charts */}
      <div className="sys-charts-grid">
        {byStatus.length > 0 && (
          <div className="sys-chart-card">
            <h4>Complaints by Status</h4>
            <BarChart data={byStatus} labelKey="status" valueKey="count" color="#1a237e" />
          </div>
        )}
        {byTaluka.length > 0 && (
          <div className="sys-chart-card">
            <h4>GPs by Taluka</h4>
            <BarChart data={byTaluka} labelKey="taluka" valueKey="count" color="#2e7d32" />
          </div>
        )}
        {byRole.length > 0 && (
          <div className="sys-chart-card">
            <h4>Users by Role</h4>
            <BarChart data={byRole} labelKey="role" valueKey="count" color="#e65100" />
          </div>
        )}
      </div>

      {/* Recent Tenants */}
      {recentTenants.length > 0 && (
        <div className="sys-card sys-dashboard-table">
          <h3>Recently Added Grampanchayats</h3>
          <div className="sys-table-wrap">
            <table className="sys-table">
              <thead>
                <tr><th>Name</th><th>Taluka</th><th>District</th><th>Slug</th><th>Status</th><th>Added</th></tr>
              </thead>
              <tbody>
                {recentTenants.map((t, i) => (
                  <tr key={t.id || i}>
                    <td><strong>{t.name}</strong></td>
                    <td>{t.taluka || '—'}</td>
                    <td>{t.district || '—'}</td>
                    <td style={{ fontFamily: 'monospace', color: '#1a237e' }}>{t.slug}</td>
                    <td><span className={`sys-badge ${t.is_active ? 'sys-badge-active' : 'sys-badge-inactive'}`}>{t.is_active ? 'Active' : 'Inactive'}</span></td>
                    <td style={{ whiteSpace: 'nowrap' }}>{t.createdAt ? new Date(t.createdAt).toLocaleDateString('en-IN') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
